import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Dropdown, Form, Button } from 'react-bootstrap'
import InventoryListComponent from '../components/InventoryListComponent'
import BulkOperationsToolbar from '../components/BulkOperationsToolbar'
import { createColumnToggleDropdown } from '../utils/tableUtils'
import { useSearchWithDebounce } from '../hooks/useSearchWithDebounce'

function ExpiringItemsWidget({ onDataUpdate }) {
    const navigate = useNavigate()
    
    // Default column visibility configuration - optimized for expiring items
    const defaultColumnVisibility = {
        selection: true,
        actions: true,
        inventory_id: false,
        stock_item_id: false,
        sku: true,
        image: true,
        gtin: true,
        upc: false,
        description: true,
        lot: true,
        notes: false,
        receivedDate: false,
        bbDate: true, // Show bbDate by default for expiring items
        warehouse: true,
        location: true,
        categories: false,
        manufacturer: false,
        countryOfOrigin: false,
        vendor: true,
        cost: false,
        totalCost: false,
        uom: false,
        itemSize: false,
        casePack: false,
        netCaseWeight: false,
        packageWeight: false,
        grossCaseWeight: false,
        ti: false,
        hi: false,
        length: false,
        width: false,
        height: false,
        volume: false,
        quantity: true,
        in_transit: false
    }

    // Column labels for the toggle dropdown
    const columnLabels = {
        selection: 'Select',
        actions: 'Actions',
        inventory_id: 'Inventory ID',
        stock_item_id: 'Stock Item ID',
        sku: 'SKU',
        image: 'Image',
        gtin: 'GTIN',
        upc: 'UPC',
        description: 'Description',
        lot: 'LOT',
        notes: 'Notes',
        receivedDate: 'Received Date',
        bbDate: 'Best Before',
        warehouse: 'Warehouse',
        location: 'Location',
        categories: 'Categories',
        manufacturer: 'Manufacturer',
        countryOfOrigin: 'Country of Origin',
        vendor: 'Vendor',
        cost: 'Cost',
        totalCost: 'Total Cost',
        uom: 'UOM',
        itemSize: 'Item Size',
        casePack: 'Case Pack',
        netCaseWeight: 'Net Case Weight',
        packageWeight: 'Package Weight',
        grossCaseWeight: 'Gross Case Weight',
        ti: 'TI',
        hi: 'HI',
        length: 'Length (inch)',
        width: 'Width (inch)',
        height: 'Height (inch)',
        volume: 'Volume',
        quantity: 'Quantity',
        in_transit: 'In Transit'
    }

    // State for column visibility with localStorage persistence
    const [columnVisibility, setColumnVisibility] = useState(() => {
        const savedVisibility = localStorage.getItem('expiringItemsColumnVisibility')
        return savedVisibility ? JSON.parse(savedVisibility) : defaultColumnVisibility;
    })

    // Save column visibility to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('expiringItemsColumnVisibility', JSON.stringify(columnVisibility));
    }, [columnVisibility])

    // Column visibility toggle handler
    const toggleColumnVisibility = (column) => {
        setColumnVisibility(prevState => {
            const newState = {
                ...prevState,
                [column]: !prevState[column]
            };
            return newState;
        });
    }

    // Expiration time window options
    const expirationWindows = [
        { label: 'Already Expired', days: 0 },
        { label: 'Next 30 Days', days: 30 },
        { label: 'Next 60 Days', days: 60 },
        { label: 'Next 90 Days', days: 90 }
    ]

    // State for selected expiration window
    const [selectedWindow, setSelectedWindow] = useState(expirationWindows[1]) // Default to Next 30 Days
    
    // Calculate date range based on selected window
    const bbDateRange = useMemo(() => {
        const today = new Date()
        const endDate = new Date(today)
        
        if (selectedWindow.days === 0) {
            // For expired items, show items with bb_date before today
            return {
                startDate: null,
                endDate: today
            }
        } else {
            // For future expiration, show items with bb_date from today to selected days
            endDate.setDate(today.getDate() + selectedWindow.days)
            return {
                startDate: today,
                endDate: endDate
            }
        }
    }, [selectedWindow.days])
    
    // State for tracking inventory items and refresh functionality
    const [inventoryItems, setInventoryItems] = useState([])
    const [refreshKey, setRefreshKey] = useState(0)
    
    // State for bulk operations
    const [selectedItems, setSelectedItems] = useState([])
    const [warehouseMap, setWarehouseMap] = useState({})
    const [clearSelectionTrigger, setClearSelectionTrigger] = useState(0)

    // Search functionality with debouncing
    const { searchTerm, searchInputValue, handleSearchInputChange, clearSearch } = useSearchWithDebounce('')

    // Configure initial filters for expiring items
    const initialFilters = {
        bbStartDate: bbDateRange.startDate?.toISOString().split('T')[0] || null,
        bbEndDate: bbDateRange.endDate?.toISOString().split('T')[0] || null,
        pageSize: 10, // Show fewer items in the widget
        sortColumn: 'bbDate', // Sort by best-before date (frontend column ID)
        sortDirection: 'desc' // Descending order (latest expiration first)
    }
    
    // Handler for data refresh after bulk operations
    const handleDataRefresh = useCallback(() => {
        // Force InventoryListComponent to refresh by updating the key
        setRefreshKey(prev => prev + 1)
    }, [])
    
    // Clear selection handler
    const handleClearSelection = useCallback(() => {
        setSelectedItems([])
        setClearSelectionTrigger(prev => prev + 1) // Trigger clear in InventoryListComponent
    }, [])
    
    // Callback to receive selected items from InventoryListComponent
    const handleSelectionChange = useCallback((selectedRows, warehouseMapping) => {
        setSelectedItems(selectedRows)
        setWarehouseMap(warehouseMapping || {})
    }, [])
    
    // Custom action handler that adds returnTo parameter
    const handleCustomAction = useCallback((actionPath, row, returnPath) => {
        const separator = actionPath.includes('?') ? '&' : '?';
        const finalPath = `${actionPath}${separator}returnTo=${encodeURIComponent('/dashboard')}`;
        navigate(finalPath);
    }, [navigate])
    
    // Update initial filters when selected window changes
    const updatedInitialFilters = useMemo(() => ({
        bbStartDate: bbDateRange.startDate?.toISOString().split('T')[0] || null,
        bbEndDate: bbDateRange.endDate?.toISOString().split('T')[0] || null,
        searchTerm: searchTerm,
        pageSize: 10,
        sortColumn: 'bbDate', // Sort by best-before date (frontend column ID)
        sortDirection: 'desc' // Descending order (latest expiration first)
    }), [bbDateRange, searchTerm])

    return (
        <Card className="h-100">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div style={{ position: 'relative', width: '250px' }}>
                        <Form.Control
                            type="text"
                            placeholder="Search inventory..."
                            value={searchInputValue}
                            onChange={handleSearchInputChange}
                            style={{ paddingRight: searchInputValue ? '30px' : '12px' }}
                            autoComplete="off"
                            spellCheck="false"
                        />
                        {searchInputValue && (
                            <Button
                                variant="link"
                                onClick={clearSearch}
                                title="Clear search"
                                style={{
                                    position: 'absolute',
                                    right: '2px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    padding: '0',
                                    width: '24px',
                                    height: '24px',
                                    border: 'none',
                                    background: 'transparent',
                                    color: '#6c757d',
                                    fontSize: '20px',
                                    lineHeight: '1',
                                    cursor: 'pointer',
                                    textDecoration: 'none'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.color = '#000'}
                                onMouseOut={(e) => e.currentTarget.style.color = '#6c757d'}
                            >
                                ×
                            </Button>
                        )}
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        {selectedItems.length > 0 && (
                            <BulkOperationsToolbar 
                                selectedItems={selectedItems}
                                inventoryItems={inventoryItems}
                                warehouseMap={warehouseMap}
                                onSelectionClear={handleClearSelection}
                                onRefreshData={handleDataRefresh}
                                className="me-2"
                            />
                        )}
                        <div>
                            <Dropdown>
                                <Dropdown.Toggle 
                                    variant="light" 
                                    id="column-toggle-dropdown"
                                    style={{
                                        fontWeight: 'bold',
                                        padding: '0rem 0.5rem 0.35rem',
                                        height: '25px'
                                    }}
                                >
                                    Toggle Columns
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {Object.keys(columnVisibility).map((column) => (
                                        <Dropdown.Item 
                                            key={column} 
                                            as="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                toggleColumnVisibility(column);
                                            }}
                                        >
                                            <Form.Check
                                                type="checkbox"
                                                id={`column-toggle-${column}`}
                                                label={columnLabels[column]}
                                                checked={columnVisibility[column]}
                                                onChange={() => toggleColumnVisibility(column)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                        <Dropdown>
                            <Dropdown.Toggle 
                                variant="light" 
                                id="expiration-window-filter-dropdown"
                                style={{
                                    fontWeight: 'bold',
                                    padding: '0rem 0.5rem 0.35rem',
                                    height: '25px'
                                }}
                            >
                                {selectedWindow.label}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {expirationWindows.map((window, index) => (
                                    <Dropdown.Item 
                                        key={index}
                                        onClick={() => setSelectedWindow(window)}
                                        active={window.days === selectedWindow.days}
                                    >
                                        {window.label}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
            </Card.Header>
            <Card.Body className="p-0">
                <InventoryListComponent
                    key={`${JSON.stringify(columnVisibility)}-${refreshKey}-${JSON.stringify(updatedInitialFilters)}-${clearSelectionTrigger}`} // Force re-render when column visibility, refresh, filters, or clear trigger changes
                    showFilters={false}
                    showBulkActions={true}
                    showColumnToggle={false}
                    returnPath="/dashboard"
                    customActionHandler={handleCustomAction}
                    initialFilters={updatedInitialFilters}
                    defaultColumnVisibility={columnVisibility}
                    onSelectionChange={handleSelectionChange}
                    clearSelectionTrigger={clearSelectionTrigger}
                    onBulkActionSuccess={handleDataRefresh}
                    className="border-0" // Remove border since we're inside a Card
                />
            </Card.Body>
        </Card>
    )
}

export default ExpiringItemsWidget
